import { useState, useEffect } from "react";
import { Search, TrendingUp, TrendingDown, DollarSign, Activity, Zap, BarChart3, Globe, Bitcoin, Scale, Trophy, Film, FlaskConical, Building2, Plane, ChevronDown, ChevronUp, RefreshCw } from "lucide-react";
import { API_BASE } from "@/lib/api";

interface Market {
  id: string;
  slug?: string;
  question: string;
  category: string;
  volume: number;
  liquidity: number;
  yes_price: number;
  no_price: number;
  spread: number;
  end_date: string;
  image_url?: string;
  description?: string;
}

const CATEGORY_CONFIG: Record<string, { icon: any; color: string; bg: string }> = {
  "Crypto": { icon: Bitcoin, color: "text-orange-400", bg: "bg-orange-500/10 border-orange-500/30" },
  "Politics": { icon: Scale, color: "text-blue-400", bg: "bg-blue-500/10 border-blue-500/30" },
  "Sports": { icon: Trophy, color: "text-green-400", bg: "bg-green-500/10 border-green-500/30" },
  "Pop Culture": { icon: Film, color: "text-pink-400", bg: "bg-pink-500/10 border-pink-500/30" },
  "Science": { icon: FlaskConical, color: "text-purple-400", bg: "bg-purple-500/10 border-purple-500/30" },
  "Business": { icon: Building2, color: "text-yellow-400", bg: "bg-yellow-500/10 border-yellow-500/30" },
  "Tech": { icon: Zap, color: "text-cyan-400", bg: "bg-cyan-500/10 border-cyan-500/30" },
  "Geopolitics": { icon: Globe, color: "text-red-400", bg: "bg-red-500/10 border-red-500/30" },
  "General": { icon: Activity, color: "text-gray-400", bg: "bg-gray-500/10 border-gray-500/30" },
};

function getCategoryConfig(category: string) {
  return CATEGORY_CONFIG[category] || CATEGORY_CONFIG["General"];
}

export default function MarketsPage() {
  const [groupedMarkets, setGroupedMarkets] = useState<Record<string, Market[]>>({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [selectedMarket, setSelectedMarket] = useState<Market | null>(null);
  const [tradeSide, setTradeSide] = useState<"YES" | "NO">("YES");
  const [tradeSize, setTradeSize] = useState(100);
  const [tradePrice, setTradePrice] = useState(0.5);
  const [expandedCategories, setExpandedCategories] = useState<Record<string, boolean>>({});
  const [lastUpdate, setLastUpdate] = useState(0);

  const fetchLive = async () => {
    try {
      setLoading(true);
      const r = await fetch(`${API_BASE}/api/markets/live`);
      const data = await r.json();
      if (data.markets_by_category) {
        setGroupedMarkets(data.markets_by_category);
        setLastUpdate(data.timestamp || Date.now());
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLive();
    const interval = setInterval(fetchLive, 10000); // Live refresh every 10 seconds
    return () => clearInterval(interval);
  }, []);

  const placeManualTrade = async () => {
    if (!selectedMarket) return;
    try {
      const r = await fetch(`${API_BASE}/api/markets/manual-trade`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          market_id: selectedMarket.id,
          side: tradeSide,
          price: tradePrice,
          size: tradeSize,
          order_type: "limit",
        }),
      });
      const data = await r.json();
      alert(data.message || `Trade ${data.status}`);
    } catch (e) {
      alert("Trade failed. Check console.");
    }
  };

  const toggleCategory = (cat: string) => {
    setExpandedCategories(prev => ({ ...prev, [cat]: !prev[cat] }));
  };

  // Calculate totals
  const totalMarkets = Object.values(groupedMarkets).flat().length;
  const cryptoMarkets = groupedMarkets["Crypto"] || [];

  if (loading && totalMarkets === 0) {
    return <div className="p-6 text-center text-gray-500 text-sm">Loading live markets...</div>;
  }

  return (
    <div className="p-4 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-lg font-bold text-white flex items-center gap-2">
            <Globe size={20} className="text-blue-400" />
            LIVE POLYMARKET MARKETS
          </h1>
          <p className="text-xs text-gray-500 mt-1">
            {totalMarkets.toLocaleString()} active markets | Auto-refresh every 10s
          </p>
        </div>
        <button onClick={fetchLive} className="terminal-btn text-xs flex items-center gap-1 border-blue-500/50 text-blue-400 hover:bg-blue-500/10">
          <RefreshCw size={12} /> REFRESH NOW
        </button>
      </div>

      {/* Search */}
      <div className="relative">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search all markets..."
          className="terminal-input w-full text-xs pl-9"
        />
      </div>

      <div className="grid grid-cols-3 gap-4">
        {/* Left: Category Panels */}
        <div className="col-span-2 space-y-3 max-h-[700px] overflow-auto">
          {Object.entries(groupedMarkets).map(([category, markets]) => {
            const catConfig = getCategoryConfig(category);
            const CatIcon = catConfig.icon;
            const isExpanded = expandedCategories[category] !== false; // default open
            
            // Apply search filter
            const filtered = search 
              ? markets.filter(m => m.question.toLowerCase().includes(search.toLowerCase()))
              : markets;
            
            if (filtered.length === 0) return null;

            return (
              <div key={category} className="terminal-window border-l-4 border-l-blue-500">
                <div 
                  className="terminal-header flex items-center justify-between cursor-pointer"
                  onClick={() => toggleCategory(category)}
                >
                  <span className="flex items-center gap-2">
                    <CatIcon size={14} className={catConfig.color} />
                    <span className={`${catConfig.color} font-bold`}>{category.toUpperCase()}</span>
                    <span className="text-xs text-gray-500">({filtered.length})</span>
                  </span>
                  {isExpanded ? <ChevronUp size={14} className="text-gray-500"/> : <ChevronDown size={14} className="text-gray-500"/>}
                </div>
                
                {isExpanded && (
                  <div className="p-2 space-y-1 max-h-[300px] overflow-auto">
                    {filtered.slice(0, 50).map((m) => (
                      <div
                        key={m.id}
                        onClick={() => {
                          setSelectedMarket(m);
                          setTradePrice(m.yes_price);
                        }}
                        className={`p-2 border rounded cursor-pointer flex items-center justify-between transition-all ${
                          selectedMarket?.id === m.id
                            ? "border-blue-500 bg-blue-500/10"
                            : "border-terminal-border hover:border-blue-500/50"
                        }`}
                      >
                        <div className="flex-1 min-w-0 pr-2">
                          <div className="text-xs text-white truncate">{m.question}</div>
                          <div className="flex items-center gap-2 mt-0.5">
                            <span className="text-[10px] text-gray-500">Vol: ${m.volume?.toLocaleString()}</span>
                            <span className="text-[10px] text-gray-500">Liq: ${m.liquidity?.toLocaleString()}</span>
                          </div>
                        </div>
                        <div className="text-right shrink-0">
                          <div className="text-xs text-blue-400 font-bold">YES {m.yes_price?.toFixed(3)}</div>
                          <div className="text-[10px] text-red-400">NO {m.no_price?.toFixed(3)}</div>
                        </div>
                      </div>
                    ))}
                    {filtered.length > 50 && (
                      <div className="text-center text-[10px] text-gray-500 py-1">
                        +{filtered.length - 50} more markets in {category}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Right: Trade Panel */}
        <div className="space-y-3">
          {/* Quick Crypto */}
          {cryptoMarkets.length > 0 && (
            <div className="terminal-window border-l-4 border-l-orange-500">
              <div className="terminal-header">
                <span className="flex items-center gap-2 text-orange-400">
                  <Bitcoin size={12} />
                  CRYPTO MARKETS ({cryptoMarkets.length})
                </span>
              </div>
              <div className="p-2 space-y-1 max-h-[200px] overflow-auto">
                {cryptoMarkets.slice(0, 10).map((m) => (
                  <div
                    key={m.id}
                    onClick={() => { setSelectedMarket(m); setTradePrice(m.yes_price); }}
                    className="p-2 border border-terminal-border rounded hover:border-orange-500/50 cursor-pointer"
                  >
                    <div className="text-xs text-white truncate">{m.question}</div>
                    <div className="flex justify-between mt-0.5">
                      <span className="text-[10px] text-blue-400">YES {m.yes_price?.toFixed(3)}</span>
                      <span className="text-[10px] text-red-400">NO {m.no_price?.toFixed(3)}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Manual Trade */}
          <div className="terminal-window">
            <div className="terminal-header">
              <span className="flex items-center gap-2 text-blue-400">
                <DollarSign size={12} />
                MANUAL TRADE
              </span>
            </div>
            <div className="p-3 space-y-3">
              {selectedMarket ? (
                <>
                  <div>
                    <label className="text-[10px] text-gray-500">SELECTED</label>
                    <div className="text-xs text-white font-medium truncate">{selectedMarket.question}</div>
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => setTradeSide("YES")} className={`flex-1 py-1.5 text-xs border rounded ${tradeSide === "YES" ? "border-blue-500 bg-blue-500/20 text-blue-400" : "border-terminal-border text-gray-400"}`}>
                      <TrendingUp size={10} className="inline mr-1"/> YES
                    </button>
                    <button onClick={() => setTradeSide("NO")} className={`flex-1 py-1.5 text-xs border rounded ${tradeSide === "NO" ? "border-red-500 bg-red-500/20 text-red-400" : "border-terminal-border text-gray-400"}`}>
                      <TrendingDown size={10} className="inline mr-1"/> NO
                    </button>
                  </div>
                  <div>
                    <label className="text-[10px] text-gray-500">PRICE</label>
                    <input type="number" step={0.001} min={0.001} max={0.999} value={tradePrice} onChange={(e) => setTradePrice(parseFloat(e.target.value))} className="terminal-input w-full mt-1 text-xs" />
                  </div>
                  <div>
                    <label className="text-[10px] text-gray-500">SIZE (USD)</label>
                    <input type="number" min={1} value={tradeSize} onChange={(e) => setTradeSize(parseFloat(e.target.value))} className="terminal-input w-full mt-1 text-xs" />
                  </div>
                  <button onClick={placeManualTrade} className="terminal-btn-primary w-full text-xs py-2 bg-blue-600 hover:bg-blue-500">
                    PLACE {tradeSide} ORDER
                  </button>
                </>
              ) : (
                <div className="text-sm text-gray-500 text-center py-4">Select a market to trade</div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
